import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CurecallToggle } from '@honestica/curecall-ui'
import { useTimezonePreference } from 'src/providers/timezone-preference.provider'

const getUtcOffsetMinutes = (timezone: string): number => {
  const now = new Date()
  const utcMs = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' })).getTime()
  const tzMs = new Date(now.toLocaleString('en-US', { timeZone: timezone })).getTime()
  return (tzMs - utcMs) / 60_000
}

const formatDiff = (diffMinutes: number): string => {
  const sign = diffMinutes >= 0 ? '+' : '-'
  const abs = Math.abs(diffMinutes)
  const hours = Math.floor(abs / 60)
  const minutes = abs % 60
  return minutes === 0 ? `${sign}${hours}h` : `${sign}${hours}h${String(minutes).padStart(2, '0')}`
}

const LocalTimeClock = ({ isCollapsed }: { isCollapsed: boolean }) => {
  const { t } = useTranslation()
  const { hasTimezoneMismatch, projectTimezone, browserTimezone, isProjectTimezoneSelected, toggleTimezone } =
    useTimezonePreference()

  const [time, setTime] = useState(() =>
    projectTimezone
      ? new Intl.DateTimeFormat(undefined, {
          timeZone: projectTimezone,
          hour: '2-digit',
          minute: '2-digit',
        }).format(new Date())
      : '',
  )

  useEffect(() => {
    if (!projectTimezone) return
    const format = () =>
      new Intl.DateTimeFormat(undefined, {
        timeZone: projectTimezone,
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date())
    setTime(format())
    const interval = setInterval(() => setTime(format()), 60_000)
    return () => clearInterval(interval)
  }, [projectTimezone])

  if (!hasTimezoneMismatch) return null

  const diff = formatDiff(
    getUtcOffsetMinutes(projectTimezone as string) - getUtcOffsetMinutes(browserTimezone),
  )

  return (
    <div className="c-mb-2 c-text-white c-text-xs c-opacity-70 c-font-nexa c-text-center">
      {!isCollapsed && <span>{t('components.navigation.local-time-label')} </span>}
      <strong>{time}</strong>
      <span> ({diff})</span>
      {!isCollapsed && (
        <div className="c-mt-1 c-flex c-items-center c-justify-center c-gap-2">
          <span>{t('components.navigation.timezone-toggle-project')}</span>
          <CurecallToggle isEnabled={isProjectTimezoneSelected} onChange={toggleTimezone} />
        </div>
      )}
    </div>
  )
}

export default LocalTimeClock
